import { parseSseFrames } from './events'
import type { Frame } from './events'

/** How the caller is told what happened. */
export interface StreamHandlers {
  onFrame: (frame: Frame) => void

  /** Connected, reconnecting, or offline — for the `aria-live` connection chip. */
  onStatus?: (status: ConnectionStatus) => void

  /** A fresh token, acquired BEFORE EVERY CONNECT AND RECONNECT. */
  token: () => Promise<string>
}

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'offline' | 'closed'

/** The deliberate recycle. Immediate, because it is expected. */
const RECYCLE_DELAY_MS = 250

/** A real failure. Exponential with jitter to a ceiling. */
const FAILURE_BASE_MS = 500
const FAILURE_CEILING_MS = 10_000

/**
 * The engagement stream. `04 §3.8`.
 *
 * <p><b>THE RECYCLE AND THE FAILURE ARE DISTINGUISHED, AND THAT IS THE WHOLE DESIGN.</b> The server
 * closes cleanly at 200 seconds having sent `retry: 250`, because 230 s is the most an
 * HTTP-triggered Function can take to respond. Reconnecting in 250 ms is right for an expected
 * close and wrong for a real one — "treating both identically either makes the recycle look like a
 * fault or makes a real outage retry too fast".</p>
 *
 * <p><b>A FRESH TOKEN BEFORE EVERY CONNECT.</b> The recycle doubles as the refresh point, which is
 * what makes `AC-AUTH-4` — a token expiring mid-engagement, refreshed silently, no content lost —
 * a property of the transport rather than a thing someone has to remember.</p>
 */
export class EngagementStream {
  private readonly url: string
  private readonly handlers: StreamHandlers
  private abort: AbortController | null = null
  private stopped = false
  private lastSeq: number
  private failures = 0

  constructor(url: string, handlers: StreamHandlers, afterSeq = 0) {
    this.url = url
    this.handlers = handlers
    this.lastSeq = afterSeq
  }

  /** Where the stream has got to, for a reconnect and for the caller's own bookkeeping. */
  get seq(): number {
    return this.lastSeq
  }

  /** Connect, and keep connected until {@link stop}. */
  async start(): Promise<void> {
    while (!this.stopped) {
      const clean = await this.connectOnce()
      if (this.stopped) break

      // AN EXPECTED CLOSE RESETS THE BACKOFF. Otherwise an engagement that lives for an hour —
      // eighteen recycles — would be treated as eighteen consecutive failures and end up waiting
      // ten seconds between perfectly healthy reconnections.
      const delay = clean ? RECYCLE_DELAY_MS : this.backoff()
      if (clean) this.failures = 0

      this.handlers.onStatus?.(clean ? 'connecting' : 'reconnecting')
      await sleep(delay)
    }

    this.handlers.onStatus?.('closed')
  }

  /** Stop, and do not reconnect. */
  stop(): void {
    this.stopped = true
    this.abort?.abort()
  }

  /**
   * One connection. Returns true when the server closed cleanly (the 200-second recycle) and false
   * when it failed.
   */
  private async connectOnce(): Promise<boolean> {
    this.abort = new AbortController()
    this.handlers.onStatus?.('connecting')

    try {
      const response = await fetch(this.url, {
        headers: {
          Authorization: `Bearer ${await this.handlers.token()}`,

          // `Last-Event-ID` IS WHAT A RECONNECTING CLIENT SENDS. The server serves everything
          // after it from one indexed single-partition query, then the client joins the live set.
          'Last-Event-ID': String(this.lastSeq),
        },
        signal: this.abort.signal,
        cache: 'no-store',
      })

      if (response.status === 401) {
        // RE-AUTHENTICATE AND COME STRAIGHT BACK. A 401 mid-engagement is an expired token, not an
        // outage, and backing off would leave the page stale for no reason.
        return true
      }

      if (!response.ok || !response.body) return false

      this.handlers.onStatus?.('open')

      for await (const frame of parseSseFrames(response.body)) {
        if (typeof frame.seq === 'number') this.lastSeq = Math.max(this.lastSeq, frame.seq)

        // THE RECYCLE FRAME IS NOT AN EVENT. It says the close that follows is expected; passing
        // it to the reducer would put a control kind into a content view.
        if (frame.kind === 'recycle') return true

        this.handlers.onFrame(frame)
      }

      // The body ended without a recycle frame. Treat it as clean anyway: the server may have been
      // scaled in mid-stream, which is ordinary and not worth a ten-second penalty.
      return true
    } catch (error) {
      if (this.stopped) return true

      // OFFLINE IS A DISTINCT STATE, and the browser tells us when it ends. `online` and
      // `visibilitychange` are extra reconnect triggers for exactly this reason.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        this.handlers.onStatus?.('offline')
      }

      return false
    } finally {
      this.abort = null
    }
  }

  /** Exponential with jitter, to a ~10 s ceiling. */
  private backoff(): number {
    this.failures += 1
    const ceiling = Math.min(FAILURE_BASE_MS * 2 ** (this.failures - 1), FAILURE_CEILING_MS)

    // FULL JITTER. Every viewer of an engagement disconnects at the same instant when a BFF
    // instance recycles, and a fixed backoff would bring them all back at the same instant too.
    return Math.random() * ceiling
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
